// ----------------------------------------------------------
// <Banner /> – a stateless, generic, pure React component
// ----------------------------------------------------------
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import React from "react";

/* -------------------------------------------------------------------------- */
/*                               <Banner />                                   */
/* -------------------------------------------------------------------------- */
export enum BannerVariant {
    INFO = "info",
    SUCCESS = "success",
    WARNING = "warning"
    // ERROR = "error"
}

type BannerVariantType = (typeof BannerVariant)[keyof typeof BannerVariant];

export interface BannerProps {
    /** Optional leading icon */
    icon?: string;
    /** Main headline */
    title: string;
    /** Optional secondary message */
    description?: string;
    /** Optional right‑aligned CTA(s) */
    children?: React.ReactNode;
    /** Visual style */
    variant?: BannerVariant;
    /** Extra utility classes */
    className?: string;
    /** Custom colors */
    colors?: Record<BannerVariantType, BannerColorPallet>;
}

type BannerColorPallet = {
    border: string;
    icon: string;
    title: string;
    description: string;
    // ctaBackground?: string;
};

const defaultColors: Record<BannerVariantType, BannerColorPallet> = {
    [BannerVariant.INFO]: {
        border: "border-purboflow-300",
        icon: "text-purboflow-300",
        title: "text-purboflow-300",
        description: "text-gray-300"
    },
    [BannerVariant.SUCCESS]: {
        border: "border-success-disabled",
        icon: "text-success-disabled",
        title: "text-success-disabled",
        description: "text-gray-300"
    },
    [BannerVariant.WARNING]: {
        border: "border-destructive-disabled",
        icon: "text-destructive-disabled",
        title: "text-destructive-disabled",
        description: "text-gray-300"
    }
    // [BannerVariant.ERROR]: {
    //     border: "border-red-50",
    //     icon: "text-red-400",
    //     title: "text-red-800",
    //     description: "text-red-700"
    // }
};

export const Banner: React.FC<BannerProps> = ({
    icon,
    title,
    description,
    children: actions,
    variant = BannerVariant.INFO,
    className = "",
    colors = defaultColors
}) => {
    return (
        <div
            className={twClassNames(
                "flex items-center justify-between gap-4 rounded border bg-gray-800 px-4 py-3",
                colors[variant].border,
                className
            )}
        >
            <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                    {icon && (
                        <span className={twClassNames("mt-0.5 shrink-0")}>
                            <i
                                className={twClassNames(
                                    "text-lg text-primary",
                                    icon,
                                    colors[variant].icon
                                )}
                            />
                        </span>
                    )}
                    <p className={twClassNames("font-medium leading-none", colors[variant].title)}>
                        {title}
                    </p>
                </div>
                {description && (
                    <p
                        className={twClassNames(
                            "mb-0.5 text-sm leading-tight opacity-80",
                            colors[variant].description
                        )}
                    >
                        {description}
                    </p>
                )}
            </div>

            {actions && <div className="shrink-0">{actions}</div>}
        </div>
    );
};
